import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Subscribe.css'; // Reuse subscription styles

const ServiceConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Get payment data and service details from location state
  const paymentData = location.state?.paymentData;
  const serviceDetails = location.state?.serviceDetails;

  useEffect(() => {
    // If no payment data or service details, redirect to home
    if (!paymentData || !serviceDetails) {
      navigate('/');
      return;
    }

    // If user not logged in, redirect to signin
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    // Track service purchase conversion
    if (typeof window.gtag !== 'undefined') {
      // Get UTM parameters if they exist
      const urlParams = new URLSearchParams(window.location.search);
      const utmData = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content')
      };

      // Track service purchase as conversion
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17465397731/POpFCI6RsYUbEOOjk4hB',
        'value': paymentData.amount || 1.0,
        'currency': 'USD',
        'transaction_id': paymentData.id || Date.now().toString(),
        'custom_parameters': {
          'service_type': getServiceTypeLabel(),
          'source': utmData.utm_source || 'direct',
          'medium': utmData.utm_medium || 'none',
          'campaign': utmData.utm_campaign || 'none',
          'purchase_completed': true
        }
      });

      // Track as purchase event for GA4
      window.gtag('event', 'purchase', {
        transaction_id: paymentData.id || Date.now().toString(),
        value: paymentData.amount || 1.0,
        currency: 'USD',
        items: [{
          item_id: serviceDetails.id || 'service',
          item_name: serviceDetails.title || 'Business Formation Service',
          category: 'business_services',
          quantity: 1,
          price: paymentData.amount || 1.0
        }]
      });
    }

  }, [paymentData, serviceDetails, currentUser, navigate]);

  // Get service type for display
  const getServiceTypeLabel = () => {
    if (!serviceDetails) return 'service';
    if (serviceDetails.title.includes('LLC')) return 'LLC Formation';
    if (serviceDetails.title.includes('Corporation')) return 'Corporation Formation';
    if (serviceDetails.title.includes('Non-Profit')) return 'Non-Profit Formation';
    if (serviceDetails.title.includes('Dissolution')) return 'Business Dissolution';
    return 'business formation service';
  };

  // Handle if navigated directly to this page without payment data
  if (!paymentData || !serviceDetails) {
    return null; // useEffect will handle redirect
  }

  return (
    <div className="subscribe-page">
      <div className="container">
        <div className="subscribe-container">
          <div className="confirmation-header">
            <div className="confirmation-icon">
              <span>✓</span>
            </div>
            <h1>Purchase Confirmed</h1>
          </div>

          <div className="confirmation-message">
            <p>Thank you for your purchase of our {getServiceTypeLabel()} service.</p>
            <p>We've sent a confirmation email to <strong>{currentUser.email}</strong> with your receipt and next steps.</p>
          </div>

          <div className="service-details">
            <h2>Purchase Details</h2>
            <div className="service-detail-item">
              <span>Service:</span>
              <span>{serviceDetails.title}</span>
            </div>
            <div className="service-detail-item">
              <span>Amount Paid:</span>
              <span>${(paymentData.amount || 0).toFixed(2)} + Filing Fees</span>
            </div>
            <div className="service-detail-item">
              <span>Order ID:</span>
              <span>{paymentData.id || 'N/A'}</span>
            </div>
            <div className="service-detail-item">
              <span>Purchase Date:</span>
              <span>{new Date(paymentData.created || Date.now()).toLocaleDateString()}</span>
            </div>
          </div>
          
          <div className="next-steps">
            <h2>What's Next?</h2>
            <ol>
              <li>
                <strong>Check your email</strong> - You'll receive a detailed email with next steps and any additional information we need to proceed with your {getServiceTypeLabel()}.
              </li>
              <li>
                <strong>Complete formation questionnaire</strong> - We'll be sending you a questionnaire to collect necessary information about your business entity.
              </li>
              <li>
                <strong>Document preparation</strong> - Our team will prepare your formation documents and initiate the filing process.
              </li>
              <li>
                <strong>Track your progress</strong> - You can check the status of your formation in your account dashboard.
              </li>
            </ol>
          </div>

          <div className="confirmation-actions">
            <Link to="/profile" className="button primary-button">
              Go to My Account
            </Link>
            <Link to="/" className="button secondary-button">
              Return Home
            </Link>
          </div>

          <div className="contact-support">
            <p>Questions about your purchase? Contact our support team at <a href="mailto:support@rkubeservices.com">support@rkubeservices.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceConfirmation; 